import {
  Button,
  Modal,
  Form,
  Select,
  Space,
  Typography,
  Input,
  Tree,
  message,
  InputNumber,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import client from '@/lib/feathers';
import type { UserGroup } from '@/types/database/userGroup';
import type UserGroupRoute from '@/types/database/userGroupRoute';
import type { RootState } from '@/redux/store';
import useService from '@/lib/hooks/useService';

import { defaultAuths, getTreeData, getUserRouters } from '../helper';
import type { AuthSettingForm, TableDataSource } from '../types';
import styles from './index.module.scss';

interface AuthSettingProps {
  type: 'add' | 'edit';
  userGroups?: UserGroup[];
  record?: TableDataSource;
  updateUserGroups: () => void;
}

const { Item, useForm } = Form;
const { Text } = Typography;

const AuthSetting = ({
  type,
  userGroups,
  record,
  updateUserGroups,
}: AuthSettingProps) => {
  const user = useSelector((state: RootState) => state.user);

  const [form] = useForm();

  const [userGroupRoutes] = useService<UserGroupRoute>({
    collectionName: 'userGroupRoute',
  });

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [editingAuth, setEditingAuth] = useState<UserGroup>();

  useEffect(() => {
    if (form && userGroups && record) {
      const foundUserGroup = userGroups.find(
        (userGroup) => userGroup._id === record._id
      );
      setEditingAuth(foundUserGroup);
    }
  }, [form, record, userGroups]);

  const handleButtonClick = () => {
    if (type === 'edit') {
      form.setFieldsValue(record);
    }
    setIsOpenModal(true);
  };

  const handleFormSubmit = async (formValues: AuthSettingForm) => {
    const { name, serviceName, checkedKeys } = formValues;

    const userRouters = getUserRouters(userGroupRoutes, checkedKeys);

    const data = {
      name,
      serviceName,
      order: type === 'add' ? user.auth.user.order + 1 : formValues.order,
      allow: userRouters,
      userRouter: userRouters,
      key: {
        checkedKeys,
      },
    };

    if (type === 'add') {
      data.serviceName = formValues.serviceName;
      try {
        await client.service('userGroup').create(data);
        setIsOpenModal(false);
        updateUserGroups();
        message.success('新增頁面權限成功!');
      } catch (error) {
        message.error('新增頁面權限失敗!');
      }
    }

    if (type === 'edit') {
      data.serviceName = editingAuth?.serviceName;
      try {
        await client.service('userGroup').patch(editingAuth?._id, data);
        setIsOpenModal(false);
        updateUserGroups();
        message.success('編輯頁面權限成功!');
      } catch (error) {
        message.error('編輯頁面權限失敗!');
      }
    }
  };

  const selectOptions = userGroupRoutes.map((route) => ({
    label: route.name,
    value: route.name,
  }));

  const treeData = getTreeData(userGroupRoutes);

  return (
    <>
      <Button
        onClick={handleButtonClick}
        disabled={
          user.auth.user.auth !== 'superAdmin' &&
          record &&
          record?.order <= user.auth.user.order
        }
      >
        {type === 'add' ? '頁面權限設定' : '編輯'}
      </Button>
      <Modal
        open={isOpenModal}
        title={type === 'add' ? '頁面權限設定' : '編輯頁面權限設定'}
        centered
        destroyOnClose
        footer={null}
        onCancel={() => setIsOpenModal(false)}
      >
        <Form
          form={form}
          preserve={false}
          layout="vertical"
          initialValues={editingAuth}
          onFinish={handleFormSubmit}
        >
          <Item
            label={<Text type="secondary">角色設定</Text>}
            name="name"
            rules={[{ required: true, message: '請輸入權限類別' }]}
          >
            <Input
              placeholder="請輸入權限類別"
              className={styles.widthFull}
              disabled={
                type === 'edit' &&
                defaultAuths.includes(form.getFieldValue('name'))
              }
            />
          </Item>
          {type === 'add' && (
            <Item
              label={<Text type="secondary">服務類型</Text>}
              name="serviceName"
              rules={[{ required: true, message: '請選擇服務類型' }]}
            >
              <Select
                className={styles.widthFull}
                placeholder="請選擇服務類型"
                options={selectOptions}
              />
            </Item>
          )}
          {user.auth.user.auth === 'superAdmin' && type === 'edit' && (
            <Item label={<Text type="secondary">位階</Text>} name="order">
              <InputNumber
                min={
                  editingAuth?.name === 'superAdmin'
                    ? 0
                    : user.auth.user.order + 1
                }
              />
            </Item>
          )}
          <Item
            label={<Text type="secondary">頁面名稱</Text>}
            name="checkedKeys"
            rules={[{ required: true, message: '請勾選頁面功能' }]}
            valuePropName="checkedKeys"
            trigger="onCheck"
            initialValue={editingAuth?.key.checkedKeys}
          >
            <Tree checkable height={458} treeData={treeData} />
          </Item>
          <Item className={styles.footerButtonsContainer}>
            <Space>
              <Button onClick={() => setIsOpenModal(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Space>
          </Item>
        </Form>
      </Modal>
    </>
  );
};

export default AuthSetting;
